/** Tabbed task details dialog: Summary (fields, steps, artifacts, actions)
 *  + Logs (self-refreshing diff-polling viewer). Polls the record while the
 *  task is active so state/progress/health stay current. */
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import ShareIcon from '@mui/icons-material/Share';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import * as bgtasksApi from '@/api/bgtasks';
import { ConsoleApiError } from '@/api/client';
import {
  TASK_STATE,
  isActiveState,
  type BackgroundTaskDetail,
  type TaskArtifact,
} from '@/api/bgtasks';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { usePermissions } from '@/hooks/usePermissions';

import { APPROVAL_ACTIONS, DETAIL_LINKS, RESULT_PANELS } from './approvalActions';
import {
  artifactActions,
  consoleTenantActions,
  initiatedByActions,
  stateActions,
  taskParamActions,
  taskTypeActions,
  tenantActions,
  visibilityActions,
  type TaskCtx,
} from './taskLinks';
import { TaskLogsTab } from './TaskLogsTab';
import {
  LinkifiedText,
  TaskProgressBar,
  TaskStateChip,
  TsValue,
  formatDuration,
  paramsEntries,
} from './taskFormat';

const DETAIL_POLL_MS = 5000;

interface Props {
  taskId: string;
  onClose: () => void;
  /** Tab to open on. Hosts that show the dialog in place (no URL of its own)
   *  pass 'logs'; otherwise the URL's `?tab=logs` decides. */
  initialTab?: 'summary' | 'logs';
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box sx={{ minWidth: 160 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {value ?? '—'}
      </Typography>
    </Box>
  );
}


/** Guest-list exports (personal data, client MCP P2-07) are never downloaded on the console: only
 *  the person who started one downloads it, in the dashboard (review batch 3 H1; the backend
 *  answers 403 `pii_export_not_on_console`). The task and its files stay listed. */
const PII_EXPORT_TASK_TYPES = new Set(['analytics_export']);
const PII_EXPORT_NOT_HERE =
  'Guest-list exports can only be downloaded by the person who started them, in the dashboard.';

function downloadErrorText(e: unknown): string {
  if (e instanceof ConsoleApiError && e.status === 403 && e.body?.code === 'pii_export_not_on_console') {
    return PII_EXPORT_NOT_HERE;
  }
  return `Could not fetch download URL: ${(e as Error).message}`;
}

function ArtifactRow({
  taskId,
  artifact,
  downloadable,
}: {
  taskId: string;
  artifact: TaskArtifact;
  /** False for a guest-list export: no download, no S3 key — the name and size only. */
  downloadable: boolean;
}) {
  const { show } = useSnackbar();
  const download = useMutation({
    mutationFn: () => bgtasksApi.artifactUrl(taskId, artifact.name),
    onSuccess: (r) => window.open(r.url, '_blank', 'noopener'),
    onError: (e) => show(downloadErrorText(e), 'error'),
  });
  return (
    <ListItem
      disablePadding
      secondaryAction={
        downloadable ? (
          <IconButton
            edge="end"
            size="small"
            aria-label={`Download ${artifact.name}`}
            onClick={() => download.mutate()}
            disabled={download.isPending}
          >
            {download.isPending ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
          </IconButton>
        ) : undefined
      }
      sx={{ py: 0.25 }}
    >
      <ListItemText
        primary={
          downloadable ? (
            <ValueActions actions={artifactActions(artifact)}>{artifact.name}</ValueActions>
          ) : (
            artifact.name
          )
        }
        secondary={`${(artifact.size / 1024).toFixed(1)} KB · ${artifact.content_type}`}
        primaryTypographyProps={{ variant: 'body2', fontFamily: 'monospace' }}
        secondaryTypographyProps={{ variant: 'caption' }}
      />
    </ListItem>
  );
}

function SummaryTab({ task }: { task: BackgroundTaskDetail }) {
  const { show } = useSnackbar();
  const queryClient = useQueryClient();
  const [confirmCancel, setConfirmCancel] = useState(false);

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['bgtask', task.id] }),
      queryClient.invalidateQueries({ queryKey: ['bgtasks'] }),
    ]);

  const approveMut = useMutation({
    mutationFn: () => bgtasksApi.approve(task.id),
    onSuccess: async () => {
      show('Step approved — task resuming.', 'success');
      await invalidate();
    },
    onError: (e) => show((e as Error).message, 'error'),
  });
  const cancelMut = useMutation({
    mutationFn: () => bgtasksApi.cancel(task.id),
    onSuccess: async (r) => {
      show(r.message, 'info');
      await invalidate();
    },
    onError: (e) => show((e as Error).message, 'error'),
  });

  const ctx: TaskCtx = { tenantId: task.tour_operator?.id ?? null };
  const progress = task.progress;
  const nextStep = task.steps?.[task.current_step];
  const customApproval = APPROVAL_ACTIONS[task.task_type];
  const resultPanel = RESULT_PANELS[task.task_type];
  const detailLink = DETAIL_LINKS[task.task_type]?.(task);
  const piiExport = PII_EXPORT_TASK_TYPES.has(task.task_type);
  // Convention: metered tasks expose a running USD total under
  // metrics.<namespace>.cost_usd (e.g. ai_import).
  const costSoFar = (() => {
    for (const value of Object.values(task.metrics ?? {})) {
      const cost = (value as { cost_usd?: unknown } | null)?.cost_usd;
      if (typeof cost === 'number') return cost;
    }
    return null;
  })();

  return (
    <Stack spacing={2}>
      {task.error_message && (
        <Alert severity="error">
          <LinkifiedText text={task.error_message} />
        </Alert>
      )}
      {task.state === TASK_STATE.RUNNING && task.health?.status === 'lost' && (
        <Alert severity="warning">
          The worker running this task stopped sending heartbeats — it was
          likely terminated. The sweeper will mark the task failed shortly.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Field
          label="Status"
          value={
            <ValueActions chip actions={stateActions(task.state, task.state_display)}>
              <TaskStateChip task={task} />
            </ValueActions>
          }
        />
        <Field
          label="Type"
          value={
            <ValueActions actions={taskTypeActions(task.task_type)}>
              <code>{task.task_type}</code>
            </ValueActions>
          }
        />
        <Field
          label="Tenant"
          value={
            task.tour_operator ? (
              <ValueActions actions={tenantActions(task.tour_operator)}>
                <Link
                  href={`/tour-operators/${task.tour_operator.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {task.tour_operator.name}
                </Link>
              </ValueActions>
            ) : (
              <ValueActions actions={consoleTenantActions()}>Console</ValueActions>
            )
          }
        />
        <Field
          label="Initiated by"
          value={
            task.user_id || task.user_email ? (
              <ValueActions actions={initiatedByActions({ id: task.user_id, email: task.user_email })}>
                {task.user_id ? (
                  <Link href={`/users/${task.user_id}`} target="_blank" rel="noopener noreferrer">
                    {task.user_email ?? `#${task.user_id}`}
                  </Link>
                ) : (
                  task.user_email
                )}
              </ValueActions>
            ) : (
              '—'
            )
          }
        />
        <Field
          label="Visibility"
          value={
            <ValueActions actions={visibilityActions(task.visibility, task.visibility_display)}>
              {task.visibility_display}
            </ValueActions>
          }
        />
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Field label="Created" value={<TsValue value={task.created_ts} />} />
        <Field label="Started" value={<TsValue value={task.started_ts} />} />
        <Field label="Finished" value={<TsValue value={task.finished_ts} />} />
        <Field label="Duration" value={formatDuration(task.duration_seconds)} />
        <Field label="Max execution" value={formatDuration(task.max_execution_time)} />
        {costSoFar !== null && (
          <Field label="Cost so far" value={`$${costSoFar.toFixed(2)}`} />
        )}
      </Box>

      {progress && (progress.total > 0 || progress.step) && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Progress{progress.step ? ` — ${progress.step}` : ''}
          </Typography>
          <TaskProgressBar progress={progress} showCounts height={8} />
        </Box>
      )}

      {task.steps && task.steps.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>Steps</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {task.steps.map((step, i) => (
              <Chip
                key={step.name}
                size="small"
                label={`${i + 1}. ${step.label ?? step.name}${step.requires_approval ? ' 🔒' : ''}`}
                color={
                  i < task.current_step || task.state === TASK_STATE.COMPLETED
                    ? 'success'
                    : i === task.current_step && isActiveState(task.state)
                      ? 'info'
                      : 'default'
                }
                variant={i === task.current_step && isActiveState(task.state) ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
        </Box>
      )}

      {(task.state === TASK_STATE.AWAITING_APPROVAL || (task.can_cancel && isActiveState(task.state))) && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {task.state === TASK_STATE.AWAITING_APPROVAL && (
            customApproval ? (
              customApproval(task)
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={() => approveMut.mutate()}
                disabled={approveMut.isPending}
              >
                Approve next step{nextStep ? `: ${nextStep.label ?? nextStep.name}` : ''}
              </Button>
            )
          )}
          {task.can_cancel && isActiveState(task.state) && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setConfirmCancel(true)}
              disabled={cancelMut.isPending}
            >
              Cancel task
            </Button>
          )}
        </Box>
      )}

      {detailLink && <Box>{detailLink}</Box>}

      {task.state === TASK_STATE.COMPLETED && resultPanel && resultPanel(task)}

      {task.artifacts && task.artifacts.length > 0 && (
        <Box>
          <Typography variant="subtitle2">Artifacts</Typography>
          {piiExport && (
            <Typography variant="body2" color="text.secondary" data-pii-export-note>
              {PII_EXPORT_NOT_HERE}
            </Typography>
          )}
          <List dense disablePadding>
            {task.artifacts.map((a) => (
              <ArtifactRow key={a.name} taskId={task.id} artifact={a} downloadable={!piiExport} />
            ))}
          </List>
        </Box>
      )}

      {task.params && Object.keys(task.params).length > 0 && (
        <ArgumentsSection params={task.params} ctx={ctx} />
      )}

      {task.metrics && Object.keys(task.metrics).length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>Metrics</Typography>
          <Box
            component="pre"
            sx={{
              m: 0, p: 1, bgcolor: 'grey.50', border: 1, borderColor: 'divider',
              borderRadius: 1, fontSize: 12, overflow: 'auto', maxHeight: 160,
            }}
          >
            {JSON.stringify(task.metrics, null, 2)}
          </Box>
        </Box>
      )}

      <ConfirmDialog
        open={confirmCancel}
        title="Cancel task?"
        message={`"${task.task_label}" will be asked to stop at the next checkpoint.`}
        confirmLabel="Cancel task"
        cancelLabel="Keep running"
        destructive
        onConfirm={() => {
          setConfirmCancel(false);
          cancelMut.mutate();
        }}
        onCancel={() => setConfirmCancel(false)}
      />
    </Stack>
  );
}

/** Task arguments as key/value rows, with the allow-listed linker on the
 *  values whose id space is known (`taskLinks.taskParamActions`). Anything
 *  unrecognised stays text — a task's `params` is free-form JSON written by
 *  its launcher, so guessing would fabricate links. The raw JSON is still one
 *  copy away, and a params bag that is not a plain object falls back to it. */
function ArgumentsSection({
  params,
  ctx,
}: {
  params: Record<string, unknown>;
  ctx: TaskCtx;
}) {
  const entries = paramsEntries(params);
  const json = JSON.stringify(params, null, 2);
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle2" gutterBottom sx={{ flexGrow: 1 }}>
          Arguments
        </Typography>
        <ValueActions actions={[act.copy(json, 'Copy arguments JSON')]}>
          <Typography variant="caption" color="primary">
            copy JSON
          </Typography>
        </ValueActions>
      </Stack>
      {entries && entries.length > 0 ? (
        <Stack spacing={0.25}>
          {entries.map(([key, text]) => {
            const actions = taskParamActions(key, params[key], params, ctx);
            return (
              <Stack key={key} direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ width: 180, flexShrink: 0, fontFamily: 'monospace' }}
                >
                  {key}
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all', minWidth: 0 }}>
                  {actions.length > 0 ? <ValueActions actions={actions}>{text}</ValueActions> : text}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <Box
          component="pre"
          sx={{
            m: 0, p: 1, bgcolor: 'grey.50', border: 1, borderColor: 'divider',
            borderRadius: 1, fontSize: 12, overflow: 'auto', maxHeight: 240,
          }}
        >
          {json}
        </Box>
      )}
    </Box>
  );
}

export function TaskDetailsDialog({ taskId, onClose, initialTab }: Props) {
  // Deep-linkable initial tab: /bgtasks/<id>?tab=logs opens straight on the
  // live log stream (launchers use it to show progress right after start).
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<'summary' | 'logs'>(
    initialTab ?? (searchParams.get('tab') === 'logs' ? 'logs' : 'summary'),
  );
  const { show } = useSnackbar();
  const navigate = useNavigate();
  const { hasPerm } = usePermissions();
  const canViewAudit = hasPerm('console_audit_view');

  const query = useQuery({
    queryKey: ['bgtask', taskId],
    queryFn: () => bgtasksApi.retrieve(taskId),
    refetchInterval: (q) =>
      q.state.data && isActiveState(q.state.data.state) ? DETAIL_POLL_MS : false,
  });
  const task = query.data;

  const copyLink = () => {
    void navigator.clipboard
      .writeText(`${window.location.origin}/bgtasks/${taskId}`)
      .then(() => show('Task link copied to clipboard.', 'success'))
      .catch(() => show('Could not copy the link.', 'error'));
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 20 }}>
        {task ? task.task_label || task.task_type : 'Task details'}
        {canViewAudit && (
          <>
            {/* Both pages are gated by console_audit_view. The Changes page
                documents this deep link ("Background Tasks deep-link here")
                but nothing offered it until now. */}
            <Tooltip title="View the record changes this run made">
              <IconButton
                onClick={() => navigate(`/changes?task_id=${taskId}`)}
                sx={{ position: 'absolute', right: 116, top: 8 }}
                size="small"
              >
                <ManageHistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View this run's audit trail (launch + every record it changed)">
              <IconButton
                onClick={() => navigate(`/audit?task_id=${taskId}`)}
                sx={{ position: 'absolute', right: 80, top: 8 }}
                size="small"
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        <Tooltip title="Copy link to this task">
          <IconButton
            onClick={copyLink}
            sx={{ position: 'absolute', right: 44, top: 8 }}
            size="small"
          >
            <ShareIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Tabs value={tab} onChange={(_, v: 'summary' | 'logs') => setTab(v)} sx={{ px: 3 }}>
        <Tab value="summary" label="Summary" />
        <Tab value="logs" label="Logs" />
      </Tabs>
      <Divider />
      <DialogContent sx={{ minHeight: 420 }}>
        {query.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {query.isError && (
          <Alert severity="error">Couldn’t load task: {(query.error as Error).message}</Alert>
        )}
        {task && tab === 'summary' && <SummaryTab task={task} />}
        {task && tab === 'logs' && <TaskLogsTab task={task} />}
      </DialogContent>
    </Dialog>
  );
}
